import React, { ReactElement } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { KeyValue } from "models/utilTypes/keyValue";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, RouteConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import { RecurringDetailsProps } from "models/propTypes/invoicePropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { NestedKeyOf } from "models/utilTypes/nestedKeyType";
import { InvoiceFormTypes } from "models/formTypes";
//#endregion
//#region components
import { DateRange, DetailsTableView, SimpleMessage, Tags } from "components/common";
import { useNavigate, useLocation } from "react-router-dom";
import { Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const RecurringDetails: React.FC<RecurringDetailsProps> = (props) => {
  const { invoice, displayType = StyleEnums.DisplayType.Info } = props;
  const dimensions = useSelector((state: AppState) => state.common.dimensions.invoiceDimensions);
  const allDimensions = dimensions.flatMap((d) => d.value);

  const location = useLocation();
  const navigate = useNavigate();

  const invoicePeriods = Array.from(
    new Set(
      invoice?.rows?.map((r) =>
        r.periodisation
          ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.PeriodisationInfo, "", {
              length: r.periodisationLength.toString(),
              from: Math.abs(r.periodisationStartOffset).toString(),
              startOffSet: `${r.periodisationStartOffset === 0 ? 0 : r.periodisationStartOffset < 0 ? 1 : 2}`,
            })
          : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.None)
      )
    )
  );

  const getDetailsList = () => {
    const detailsList: Array<KeyValue<undefined, string | ReactElement> | typeof ACTION_SEPARATOR | undefined> = [
      new KeyValue(undefined, invoice?.recurringDescription ?? ""),
      new KeyValue(
        undefined,
        (
          <SimpleMessage icon={IconDefinitionConstants.WMoney} displayType={displayType}>
            <Grid className={cssExports.textBlack}>
              {LocalizationHelperInstance.getFormattedCurrency(invoice?.totalAmount ?? 0, invoice?.currency)}
            </Grid>
          </SimpleMessage>
        )
      ),
      new KeyValue(
        undefined,
        (
          <SimpleMessage icon={IconDefinitionConstants.WRecurring} displayType={displayType}>
            <Grid className={cssExports.textBlack}>
              {invoice?.invoiceFrequency !== undefined
                ? LocalizationHelperInstance.getFormattedMessage(
                    `Invoice.RecurringFrequency_${invoice?.invoiceFrequency}`
                  )
                : ""}
            </Grid>
          </SimpleMessage>
        )
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        undefined,
        (
          <SimpleMessage icon={IconDefinitionConstants.WCalendar} displayType={displayType}>
            <Grid className={cssExports.textBlack}>
              <DateRange start={invoice?.firstPostingDate} end={invoice?.stopDate} />
            </Grid>
          </SimpleMessage>
        )
      ),
      invoice?.dimensions?.length ? ACTION_SEPARATOR : undefined,
      invoice?.dimensions?.length
        ? new KeyValue(
            undefined,
            (
              <SimpleMessage icon={IconDefinitionConstants.WHashtag} displayType={displayType}>
                <Grid className={cssExports.textBlack}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
                </Grid>
              </SimpleMessage>
            )
          )
        : undefined,
      invoice?.dimensions?.length
        ? new KeyValue(
            undefined,
            (
              <Tags
                tags={
                  (invoice?.dimensions
                    .map((d) => allDimensions.find((ad) => ad.id === d)?.name)
                    .filter((d) => d !== undefined) as string[]) ?? []
                }
              />
            )
          )
        : undefined,
      ACTION_SEPARATOR,
      new KeyValue(
        undefined,
        (
          <SimpleMessage icon={IconDefinitionConstants.WCalendarTime} displayType={displayType}>
            <Grid className={cssExports.textBlack}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual)}
            </Grid>
          </SimpleMessage>
        )
      ),

      new KeyValue(
        undefined,
        (
          <>
            {invoicePeriods.length > 1 ? (
              <>
                <span>{`${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Invoice.MultiplePeriodisations
                )}: `}</span>
                <br />
              </>
            ) : (
              ""
            )}
            {invoicePeriods.map((ip, index) => (
              <>
                <span>{`${ip}${index < invoicePeriods.length - 1 ? "," : ""}`}</span>
                <br />
              </>
            ))}
          </>
        )
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        undefined,
        (
          <SimpleMessage icon={IconDefinitionConstants.WPaperPlane} displayType={displayType}>
            <Grid className={cssExports.textBlack}>
              {LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Invoice.RecurringSentInvoicesInfo,
                undefined,
                {
                  sent: `${invoice?.numberOfSentInvoices}`,
                  planned: invoice?.stopDate !== undefined ? `${invoice?.numberOfPlannedInvoices}` : "undefined",
                }
              )}
            </Grid>
          </SimpleMessage>
        )
      ),
      new KeyValue(
        undefined,
        (
          <SimpleMessage icon={IconDefinitionConstants.WCosts2} displayType={displayType}>
            <Grid className={cssExports.textBlack}>{`${LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.RecurringTotalIncome
            )}: ${LocalizationHelperInstance.getFormattedCurrency(
              invoice?.expectedRevenue ?? 0,
              invoice?.currency
            )}`}</Grid>
          </SimpleMessage>
        )
      ),
    ];

    return detailsList;
  };

  const onClickAllInfo = () => {
    const recurringSettingsFieldName: NestedKeyOf<InvoiceFormTypes.Create> = "recurringSettings";
    navigate(UrlHelper.formatPath(`/Invoice/Create?${RouteConstants.QueryParamStrings.Recurring}=${invoice?.id}`), {
      state: {
        background: location,
        focusField: recurringSettingsFieldName,
      },
    });
  };

  return (
    <DetailsTableView
      detailsList={getDetailsList()}
      buttonText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.EditInvoiceSettings)}
      buttonOnClick={onClickAllInfo}
      hideButton={props.invoice?.status === WintSuperkollAPIModel.RecurringInvoiceState.Stopped}
    ></DetailsTableView>
  );
};

export default RecurringDetails;
